import React, { useCallback, useEffect, useMemo, useState, Dispatch, SetStateAction } from 'react';
import { Tabs } from 'antd';
import styles from './UnitEditForm.module.scss';
import Button from '../../../../components/button/Button';
import Checkbox from 'src/components/checkbox/Checkbox';
import { useQueryDictionary } from '@hooks/useQueryDictionary';
import { useRolesPermissions } from '@hooks/useRolesPermission';
import { isEmptyObject, validateIsEmpty } from '@utils/validation';
import { useHistory } from 'react-router-dom';
import DeleteModal from '../delete-modal/DeleteModal';
import {
  UnitContentFragment,
  HeaUnitContentFragment,
  useGetUrlLazyQuery,
  useDeleteUnitMutation,
  useHeaDeleteUnitMutation,
  useIsValidUnitUniqueNameLazyQuery
} from '@api/graphql-codegen';
import Spinner from '@components/spinner/Spinner';
import UnitEditTabContent, { ValidationErrorKeys, validationErrors } from './TabContent';
import { EditUnit as EditUnitType, UserRoleEnum } from '@src/types';
import { useToast } from '@src/hooks/useToast';

export type EditUnit = EditUnitType;

export type CustomHeaUnit = Omit<HeaUnitContentFragment, 'id' | 'coverImage'> & { coverImage?: string | File | null, id?: string }

type Props = {
  unit?: UnitContentFragment;
  heaUnit?: HeaUnitContentFragment;
  onSave: (p: EditUnit, isHomeEd?: boolean, heaUnit?: EditUnit) => Promise<void>;
  loading: boolean;
  setLoading: (state: boolean) => void;
  toggleIsHomeEd?: (isHomeEd: boolean, heaUnit?: CustomHeaUnit) => void;
};

export enum TabKeys {
  School = "School",
  HomeEd = "Home Ed"
}

const defaultEditUnit: EditUnit = {
  name: '',
  skill: '',
  type: '',
  default: false
};

function UnitEditForm(props: Props) {
  const { onSave, loading, unit, heaUnit, setLoading, toggleIsHomeEd } = props;

  const [getUrl, { loading: urlLoading }] = useGetUrlLazyQuery({
    fetchPolicy: 'network-only',
  });

  const skillOptions = useQueryDictionary({ dictionary: 'Skills' });
  const coursesOptions = useQueryDictionary({ dictionary: 'Programs' });
  const history = useHistory();

  const [unitContent, setUnitContent] = useState<EditUnit | undefined>();
  const [heaUnitContent, setHeaUnitContent] = useState<EditUnit | undefined>();

  const [unitErrors, setUnitErrors] = useState<Partial<Record<ValidationErrorKeys, any>>>(validationErrors);
  const [heaUnitErrors, setHeaUnitErrors] = useState<Partial<Record<ValidationErrorKeys, any>>>(validationErrors);

  const [showDeleteModal, setShowDeleteModal] = useState<string | undefined>();
  const [isHomeEd, setIsHomeEd] = useState(false);

  const [currentTab, setCurrentTab] = useState<string>(TabKeys.School);
  const [fileUploadingPercent, setFileUploadingPercent] = useState(0);
  const isEdit = !!unit;

  const isContentAdmin = useRolesPermissions([UserRoleEnum.ContentAdmin]);
  const { successToast } = useToast();

  const [deleteUnit, {loading: removeLoading}] = useDeleteUnitMutation({
    onCompleted() {
      setShowDeleteModal(undefined);
      successToast('Unit is deleted successfully!');
      history.push('/super/units');
    },
  });

  const [heaDeleteUnit, {loading: heaRemoveLoading}] = useHeaDeleteUnitMutation({
    update() {
      setHeaUnitContent(undefined);
      setIsHomeEd(false);
    },
    onCompleted() {
      setShowDeleteModal(undefined);
      successToast('HEA Unit is deleted successfully!');
    },
  });

  const [isUnitUnique] = useIsValidUnitUniqueNameLazyQuery({ fetchPolicy: 'network-only'});

  useEffect(() => {
    if (unit) {
      if (unit.__typename === 'Unit') {
        setUnitContent({ ...unit });
      }
    } else if (!unitContent) {
      setUnitContent(defaultEditUnit);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  useEffect(() => {
    if (heaUnit) {
      if (heaUnit.__typename === 'HEAUnit') {
        setHeaUnitContent({ ...heaUnit });
        setIsHomeEd(true);
      }
    } else {
      setIsHomeEd(false);
    }
  }, [heaUnit]);

  const onChangeUnitContent = useCallback((content: Partial<EditUnit>) => {
    setUnitContent(data => ({ ...data, ...content } as EditUnit));
  }, []);

  const onChangeHeaUnitContent = useCallback((content: Partial<EditUnit>) => {
    setHeaUnitContent(data => ({ ...data, ...content }));
  }, []);

  const validate = useCallback(async (content?: EditUnit) => {
    const errors: Partial<Record<ValidationErrorKeys, string>> = {};

    if (validateIsEmpty(content?.name)) {
      errors.name = 'Please add a name.';
    }

    if (content?.uniqueName) {
      const data = (await isUnitUnique({
        variables: {
          input: {
            id: content.id,
            uniqueName: content.uniqueName
          }
        }
      }));

      if (!data.data?.isValidUnitUniqueName) {
        errors.uniqueName = `The same unit name exists.`;
      }
    }

    if (!isEdit) {
      if (validateIsEmpty(content?.subHeading)) {
        errors.subHeading = 'Please add a sub heading.';
      }
      if (validateIsEmpty(content?.pace)) {
        errors.pace = 'Please add a suggested pace.';
      }
      if (content?.__typename === 'Unit') {
        if (!(content as UnitContentFragment | undefined)?.teacherBox) {
          errors.teacherPreps = 'Please add a item.';
        }
      } else if (content?.__typename === 'HEAUnit') {
        if (!(content as HeaUnitContentFragment | undefined)?.parentBox) {
          errors.parentPreps = 'Please add a item.';
        }
      }
      if (!content?.box1) {
        errors.answers1 = 'Please add a item.';
      }
      if (!content?.box2) {
        errors.answers2 = 'Please add a item.';
      }
    }
    return errors;
  }, [isEdit, isUnitUnique]);
  const isHomeEdTabSelected = useMemo(() => currentTab === 'Home Ed', [currentTab]);
  const getValidUnitContent = useCallback((content?: EditUnit, isHomeEdContent?: boolean) => {
    if (!content) return content;
    if (isHomeEdContent) {
      return ({
        ...content,
        parentImage: content.parentImage === undefined ? content.teacherImage : content.parentImage,
        skillsHeading: content.skillsHeading || 'This Unit teaches skills for:',
        helpfulHeading: content.helpfulHeading || 'This unit is helpful for students who:',
        sensitiveTooltip: content.sensitiveTooltip || 'This Unit contains sensitive content. We recommend previewing the lessons to ensure you are comfortable with material.',
      });
    }
    return ({
      ...content,
      teacherImage: content.teacherImage || 'View Unit Preparation Materials:',
      skillsHeading: content.skillsHeading || 'This Unit teaches skills for:',
      helpfulHeading: content.helpfulHeading || 'This unit is helpful for students who:',
      sensitiveTooltip: content.sensitiveTooltip || 'This Unit contains sensitive content. We recommend previewing the lessons to ensure you are comfortable with material.',
      description: content.description || 'This Lesson requires the Teacher Key, which contains additional information that is necessary to teach the Lesson. Please download this document and have it available while running the Lesson.',
      unitStudyHeading: content.unitStudyHeading || 'This Study Guide helps Teachers:'
    });
  }, []);

  const saveContent = useCallback(async (content: EditUnitType, setError: Dispatch<SetStateAction<Partial<Record<ValidationErrorKeys, any>>>>) => {
    setError({});
    const errors = await validate(content);

    if (isEmptyObject(errors)) {
      setLoading(true);
      await onSave(getValidUnitContent(content, isHomeEdTabSelected) as EditUnit, isHomeEdTabSelected);
    } else {
      setError(errors);
    }
  }, [getValidUnitContent, isHomeEdTabSelected, onSave, setLoading, validate]);

  const handleSave = useCallback(async () => {
    if (!isEdit) {
      setUnitErrors({});
      setHeaUnitErrors({});
      const errors = await validate(unitContent);
      const heaErrors = await validate(heaUnitContent);
      if (isEmptyObject(errors) && (heaUnitContent && heaUnit ? isEmptyObject(heaErrors) : true)) {
        if (unitContent) {
          setLoading(true);
          await onSave(getValidUnitContent(unitContent) as EditUnit, isHomeEd, getValidUnitContent(heaUnitContent, true));
        }
      } else {
        setUnitErrors(errors);
        setHeaUnitErrors(heaErrors);
      }
    } else if (!isHomeEdTabSelected) {
      if (unitContent?.id) {
        await saveContent(unitContent, setUnitErrors);
      }
    } else {
      if (heaUnitContent?.id) {
        await saveContent(heaUnitContent, setHeaUnitErrors);
      }
    }
  }, [getValidUnitContent, heaUnitContent, isEdit, isHomeEd, heaUnit, isHomeEdTabSelected, onSave, saveContent, setLoading, unitContent, validate]);

  const handleCancel = () => {
    history.replace('/super/units')
  };

  const handleOpenModal = useCallback(() => setShowDeleteModal(currentTab), [currentTab]);
  const handleCloseModal = useCallback(() => {
    setShowDeleteModal(undefined)
  }, []);

  const handleDelete = useCallback(async () => {
    if (showDeleteModal === TabKeys.HomeEd) {
      if (heaUnit?.id) {
        await heaDeleteUnit({
          variables: {
            input: { id: heaUnit.id },
          },
        });
        setCurrentTab(TabKeys.School);
      }
    } else if (showDeleteModal === TabKeys.School) {
      if (unit?.id) {
        await deleteUnit({
          variables: {
            input: { id: unit.id },
          },
        });
        setCurrentTab(TabKeys.School);
      }
    }
  }, [deleteUnit, heaDeleteUnit, heaUnit?.id, showDeleteModal, unit?.id]);

  const onChangeTab = useCallback((tabKey: string) => {
    setCurrentTab(tabKey);
  }, []);

  const onIsHomeEdChange = useCallback((v: boolean) => {
    if (v && !heaUnitContent && unitContent) {
      // Seed the Home Ed unit by copying only the fields hea_unit actually has. This used
      // to destructure a denylist off the school unit and spread the rest, so every column
      // added to `unit` since then leaked into heaCreateUnit and was rejected by
      // HEACreateUnitInput. Widening that input would not help - heaRepository.createUnit
      // inserts the input verbatim, so an unknown key becomes a SQL error instead.
      const { teacherBox, teacherImage } = unitContent;
      const heaUnitSeed = {
        name: unitContent.name,
        subHeading: unitContent.subHeading,
        coverImage: unitContent.coverImage,
        popupCover: unitContent.popupCover,
        pace: unitContent.pace,
        topics: unitContent.topics,
        tags: unitContent.tags,
        box1: unitContent.box1,
        box2: unitContent.box2,
        description: unitContent.description,
        parentBox: teacherBox,
        parentImage: teacherImage,
        skillsHeading: unitContent.skillsHeading,
        helpfulHeading: unitContent.helpfulHeading,
        ageGroup: unitContent.ageGroup,
        sensitive: unitContent.sensitive,
        sensitiveTooltip: unitContent.sensitiveTooltip,
      };
      setHeaUnitContent(heaUnitSeed as CustomHeaUnit)
      if (unitContent.id) {
        toggleIsHomeEd?.(v, { ...heaUnitSeed, originalUnitId: unitContent.id } as CustomHeaUnit);
      }
      setIsHomeEd(v);
    } else if (!v && heaUnitContent) {
      setShowDeleteModal(TabKeys.HomeEd);
    } else {
      setIsHomeEd(v);
    }
  }, [heaUnitContent, toggleIsHomeEd, unitContent]);

  const tabItems = useMemo(() => isHomeEd ? [{
    label: TabKeys.School,
    key: TabKeys.School,
    children: unitContent ? <UnitEditTabContent
      unit={unitContent}
      onChange={onChangeUnitContent}
      getUrl={getUrl}
      setLoading={setLoading}
      skillOptions={skillOptions}
      coursesOptions={coursesOptions}
      fileUploadingPercent={fileUploadingPercent}
      setFileUploadingPercent={setFileUploadingPercent}
      setErrors={setUnitErrors}
      errors={unitErrors}
    /> : null,
    disabled: loading || urlLoading || removeLoading || heaRemoveLoading,
  }, {
    label: TabKeys.HomeEd,
    key: TabKeys.HomeEd,
    children: heaUnitContent ? <UnitEditTabContent
      unit={heaUnitContent}
      onChange={onChangeHeaUnitContent}
      getUrl={getUrl}
      setLoading={setLoading}
      skillOptions={skillOptions}
      coursesOptions={coursesOptions}
      fileUploadingPercent={fileUploadingPercent}
      setFileUploadingPercent={setFileUploadingPercent}
      setErrors={setHeaUnitErrors}
      errors={heaUnitErrors}
      isHomeEdUnit
      /> : null,
    disabled: loading || urlLoading || removeLoading || heaRemoveLoading,
  }] : [{
    label: TabKeys.School,
    key: TabKeys.School,
    children: unitContent ? <UnitEditTabContent
      unit={unitContent}
      onChange={onChangeUnitContent}
      getUrl={getUrl}
      setLoading={setLoading}
      skillOptions={skillOptions}
      coursesOptions={coursesOptions}
      fileUploadingPercent={fileUploadingPercent}
      setFileUploadingPercent={setFileUploadingPercent}
      setErrors={setUnitErrors}
      errors={unitErrors}
      /> : null,
    disabled: loading || urlLoading || removeLoading || heaRemoveLoading,
  }], [coursesOptions, fileUploadingPercent, getUrl, heaRemoveLoading, heaUnitContent, heaUnitErrors, isHomeEd, loading, onChangeHeaUnitContent, onChangeUnitContent, removeLoading, setLoading, skillOptions, unitContent, unitErrors, urlLoading]);

  return (
    <>
      <div>
        <div className={styles.row}>
          <div className={styles.actions}>
            <div className={styles.title}>
              {isEdit ? 'Unit Overview' : 'Create Unit'}
            </div>
            <div className={styles.buttons}>
              <Button className={styles.saveButton} disabled={loading || urlLoading} isLoading={loading || urlLoading} leftIcon={loading || urlLoading ? <Spinner width={20} height={20} /> : undefined} onClick={handleSave} size={'small'} label={'SAVE'}/>
              <Button className={styles.cancelButton} disabled={loading || urlLoading} onClick={handleCancel} size={'small'} label={'Cancel'}/>
            </div>
          </div>
        </div>
        <Tabs tabBarStyle={{ justifyContent: 'space-between' }} className={styles.tabs} items={tabItems} onChange={onChangeTab} tabBarExtraContent={{ right: <div><Checkbox id={'isHomeEdUnit'} checked={isHomeEd} onChange={onIsHomeEdChange} disabled={loading || urlLoading || removeLoading || heaRemoveLoading} label={'Create HomeEd Version'}/></div> }} />
        {isEdit &&
        <div className={styles.deleteRow}>
          <div className={styles.subTitle}>Warning</div>
          <Button className={styles.deleteButton} disabled={loading || urlLoading || isContentAdmin} onClick={handleOpenModal} size={'small'} label={'DELETE UNIT'}/>
        </div>}
      </div>
      {showDeleteModal && <DeleteModal open={!!showDeleteModal} loading={removeLoading || heaRemoveLoading || loading || urlLoading} onClose={handleCloseModal} onDelete={handleDelete} unit={unit} isHomeEd={showDeleteModal === TabKeys.HomeEd} />}
    </>
  );
}

export default UnitEditForm;
